// Helpers for selecting invoice file(s) from a worksheet payload.
//
// Schema versions:
//   "1.0" (or missing) — legacy: single invoiceFile, no invoiceFiles.
//   "1.1"              — dual-write: invoiceFiles canonical, invoiceFile kept
//                        populated for upstream back-compat.
//   "2.0"              — current writer: invoiceFiles only. invoiceFile may
//                        still appear in archival data.
//
// Reading rule: 1.0 → wrap invoiceFile; anything else → invoiceFiles.

type InvoiceSource = {
  schemaVersion?: string;
  invoiceFile?: unknown;
  invoiceFiles?: unknown[];
};

export const getInvoiceFiles = (data: InvoiceSource | null | undefined): unknown[] => {
  if (!data) return [];
  const version = data.schemaVersion ?? '1.0';
  if (version === '1.0') {
    return data.invoiceFile ? [data.invoiceFile] : [];
  }
  return Array.isArray(data.invoiceFiles) ? data.invoiceFiles : [];
};

// Upgrade a rehydrated worksheet payload to schema 2.0 in place on the returned
// object. Used by any flow that loads a previously-saved/submitted worksheet
// back into the form (today: draft rehydration).
//
// - 1.0 → synthesize invoiceFiles from the legacy singular.
// - 1.1 → invoiceFiles already present; drop the singular.
// - 2.0 → no-op.
export const upgradeFormDataToV2 = <T extends InvoiceSource & Record<string, unknown>>(formData: T): T => {
  const version = formData.schemaVersion ?? '1.0';
  if (version === '2.0') return formData;

  const hasInvoiceFiles = Array.isArray(formData.invoiceFiles) && formData.invoiceFiles.length > 0;
  if (!hasInvoiceFiles) {
    formData.invoiceFiles = formData.invoiceFile ? [formData.invoiceFile] : [];
  }
  delete formData.invoiceFile;
  formData.schemaVersion = '2.0';
  return formData;
};
